import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import querystring from 'querystring';
import * as assets from '../utils/assets';
import useVehicleModels, { VehicleModel } from './queries/useVehicleModels';
import useVehicleOptions, { VehicleOption } from './queries/useVehicleOptions';
import useConfigurationSummary from './queries/useConfigurationSummary';
import useOrderDetail from './queries/useOrderDetail';
import useUser from './queries/useUser';
import { ModelOptions } from '../pages/types/ModelOptions';
import { ModelProps } from '../pages/types/ModelProps';
import useVehicleModelDetails, {
  VehicleModelDetails
} from './queries/useVehicleModelDetails';
import UIError from 'client/utils/UIError';
import PATHS from 'client/utils/paths';
import _ from 'client/constants/gettext';
import url from 'client/utils/url';

// Populate default params as needed
// Return default in case the provided param is invalid
const getSelectedOptionCodes = (
  location,
  vehicleModelDetails?: VehicleModelDetails
) => {
  // TODO: Replace with URLSearchParams + polyfill
  const params = querystring.parse(location.search.replace(/\?/g, ''));
  const exteriorDefault =
    vehicleModelDetails?.vehicleConfigurationAssets?.exterior?.color
      ?.typeCodes?.[0];
  const interiorDefault =
    vehicleModelDetails?.vehicleConfigurationAssets?.interior?.color
      ?.typeCodes?.[0];
  const wheelDefault =
    vehicleModelDetails?.vehicleConfigurationAssets?.wheel?.typeCodes?.[0];

  return {
    EXC: {
      value: params.EXC || exteriorDefault,
      default: exteriorDefault
    },
    INT: {
      value: params.INT || interiorDefault,
      default: interiorDefault
    },
    WHL: {
      value: params.WHL || wheelDefault,
      default: wheelDefault
    }
  };
};
/**
 * - Parses the querystring to determine the codes for user's selected model and options
 * - Gets the full model list (get model id)
 * - Gets the selected model's options (get option ids)
 * - Gets the selected model and option configuration summary
 */
export default function useConfigurationSelection() {
  // location can be used as a hook dependency
  const location = useLocation();
  const history = useHistory();
  const { region } = useParams<{ region: string }>();
  // TODO: Replace with URLSearchParams + polyfill
  const params = querystring.parse(location.search.replace(/\?/g, ''));

  const modelCode = params.VEH || 'FF91FNA-ALLIANCE';
  const orderNum = params.ORDER;
  let selectedModel: VehicleModel | undefined;
  const [selectedVehicleOptions, setSelectedVehicleOptions] = useState<
    VehicleOption[] | undefined
  >();

  // Split model code/edition, if applicable
  const { vehicleModels, loading: loadingVehicleModels } = useVehicleModels();

  if (vehicleModels) {
    // Get selected model from vehicle models list
    selectedModel = vehicleModels.find(
      (x) => x.fullModelCode === String(modelCode).toUpperCase()
    );

    // redirect back to model selection page if modelCode is not correct
    if (!selectedModel) {
      history.push(`/${region}${PATHS.DESIGN}`);
    }
  }

  // Skip query until dependencies are ready
  const { vehicleModelDetails, loading: loadingVehicleModelDetails } =
    useVehicleModelDetails(selectedModel?.id || 0, {
      skip: !selectedModel?.id
    });
  const { vehicleOptions, loading: loadingVehicleOptions } = useVehicleOptions(
    selectedModel?.id || 0,
    {
      skip: !selectedModel?.id
    }
  );

  const { configurationSummary, loading: loadingConfigurationSummary } =
    useConfigurationSummary(
      {
        vehicleModelId: selectedModel?.id,
        vehicleOptionIds: selectedVehicleOptions?.map((o) => o?.id),
        orderNum: orderNum ? String(orderNum) : undefined
      },
      {
        skip: !selectedModel?.id || !selectedVehicleOptions
      }
    );

  // Pre-order logic
  const { user, loading: loadingUser } = useUser();
  const { order, loading: loadingOrder } = useOrderDetail(String(orderNum), {
    skip: !orderNum || !user
  });

  const [modelOptions, setModelOptions] = useState<ModelOptions>();
  const [selectedModelProps, setSelectedModelProps] = useState<ModelProps>();

  // Handle invalid order parameter
  useEffect(() => {
    if (orderNum && user && !loadingOrder && !order) {
      throw new UIError('Invalid order number', {
        message: _('LABEL_INVALID_PREORDER'),
        link: url.getSameRegionPath(`${PATHS.FF91_DESIGN_PREFIX}/model`)
      });
    }
  }, [orderNum, user, loadingOrder, order]);

  // Filter selected options
  useEffect(() => {
    const updateSelectedOptions = async () => {
      if (
        !loadingVehicleOptions &&
        vehicleOptions &&
        !loadingVehicleModelDetails &&
        vehicleModelDetails
      ) {
        const codes = getSelectedOptionCodes(location, vehicleModelDetails);
        const options: VehicleOption[] = [];
        const validatedCodes: { [key: string]: string } = {};
        Object.keys(codes).forEach((key) => {
          // Filter options by code to get selected option
          let option = vehicleOptions.find(
            (o) => codes[key].value === o.vehicleOptionType.typeCode
          );
          let validatedCode = codes[key].value;

          if (!option) {
            // Use default code if provided code is invalid
            option = vehicleOptions.find(
              (o) => codes[key].default === o.vehicleOptionType.typeCode
            );
            validatedCode = codes[key].default;
          }

          options.push(option as VehicleOption);
          validatedCodes[key] = validatedCode;
        });
        setSelectedVehicleOptions(options);
        setModelOptions(assets.mapToModelOptionsProps(vehicleModelDetails));
        setSelectedModelProps(
          assets.mapToSelectedModelProps(
            vehicleModelDetails,
            validatedCodes.EXC,
            validatedCodes.WHL,
            validatedCodes.INT
          )
        );
      }
    };

    updateSelectedOptions();
  }, [
    loadingVehicleOptions,
    vehicleOptions,
    location,
    loadingVehicleModelDetails,
    vehicleModelDetails
  ]);

  return {
    vehicleModels,
    selectedModel,
    selectedModelProps,
    vehicleOptions,
    vehicleModelDetails,
    modelOptions,
    selectedVehicleOptions,
    configurationSummary,
    order,
    loading:
      loadingConfigurationSummary ||
      loadingVehicleModels ||
      loadingVehicleOptions ||
      loadingOrder ||
      loadingUser ||
      loadingVehicleModelDetails
  };
}
